import { ApiPreviewTender, ITenderHighlight, PreviewTenderBoxes, PreviewTenderDataKeys } from './types';
import { IUserName } from 'src/models/users/types';
import { IBidIdentifier } from 'src/models/bids/Bids/types';
import { IApiTenderBox, ITenderLot, isTenderBox } from '../Tender/types';
import { convertApiLotsToLots, filterBoxes } from '../Tender/helpers';
import { isKeyOfObject } from 'src/helpers';
import { ProcurementStatus } from 'src/models/procurements/types';
import { TenderBox } from '..';
import { User } from 'src/models/users';
import { TendersItemCustomData } from '../Tenders/types';

const BOXES_NAMES: PreviewTenderDataKeys[] = [
  'availableDate',
  'contractStartAndEndStart',
  'deadline',
  'deadlineOfClarificationQuestions',
  'name',
  'introduction',
  'description',
  'scopeOfContract',
  'buyerBoxes',
  'contractDurationBoxes',
  'locationBoxes',
  'placeOfPerformanceNutsCode',
  'linkToProcurerBoxes',
  'contractValueBoxes',
  'contractRenewalBoxes',
  'typeOfProcedureBoxes',
  'mainCpvCode',
  'additionalCpvCode'
];
type RawBoxesRecord = Record<PreviewTenderDataKeys, IApiTenderBox[] | IApiTenderBox | undefined>;
export class TenderPreviewData {
  public readonly id: string;
  public readonly boxes: PreviewTenderBoxes;
  public readonly isRead: boolean;
  public readonly isFilledData: boolean;
  public readonly isInProgress: boolean;
  public readonly procurementStatus: ProcurementStatus[];
  public readonly isRejected: boolean;
  public readonly isCommented: boolean;
  public readonly assignedTo: IUserName | null;
  public readonly bid?: IBidIdentifier;
  public readonly highlights: ITenderHighlight[];
  public readonly nextId?: string;
  public readonly matchedCpvs?: string[];
  private apiTender: ApiPreviewTender;
  private tenderLots: ITenderLot[];

  constructor({
    tender: apiTender,
    isCommented,
    highlights,
    nextId,
    matchedCpvs
  }: Omit<TendersItemCustomData, 'highlights'> & { tender: ApiPreviewTender; highlights: ITenderHighlight[] }) {
    this.apiTender = apiTender;
    this.tenderLots = convertApiLotsToLots(this.apiTender.lots);
    this.id = this.apiTender.id;
    this.boxes = this.toBoxes();
    this.isRead = this.apiTender.isRead;
    this.isFilledData = this.apiTender.isFilledData;
    this.isInProgress = this.apiTender.isInProgress;
    this.procurementStatus = this.apiTender.procurementStatus;
    this.isRejected = !!this.apiTender.rejected;
    this.isCommented = isCommented;
    this.assignedTo = this.apiTender.assignedTo ? new User(this.apiTender.assignedTo) : null;
    this.bid = !!this.apiTender.bids.length
      ? {
          id: this.apiTender.bids[0].id,
          wsId: this.apiTender.bids[0].workspace.id
        }
      : undefined;
    this.highlights = highlights;
    this.nextId = nextId;
    this.matchedCpvs = matchedCpvs;
  }

  private toBoxes(): PreviewTenderBoxes {
    const general = this.apiTender.general;
    const timeline = this.apiTender.timeline;
    const rawBoxes: RawBoxesRecord = {} as RawBoxesRecord;

    BOXES_NAMES.forEach(boxName => {
      rawBoxes[boxName] = isKeyOfObject(boxName, general)
        ? general[boxName]
        : isKeyOfObject(boxName, timeline)
        ? timeline[boxName]
        : undefined;
    });

    return Object.fromEntries(
      Object.entries(rawBoxes).map(([key, value]) => {
        const tempBox =
          Array.isArray(value) && !!value.length
            ? filterBoxes(value, this.tenderLots)
            : isTenderBox(value)
            ? [new TenderBox(value)]
            : undefined;

        return [key, tempBox];
      })
    ) as PreviewTenderBoxes;
  }
}
